import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Utils } from 'core/utils';
import { HttpService } from 'core/services/http';
import {
    Box,
    Paper,
    Typography,
    Button,
    Divider,
    Chip,
    IconButton,
    Tooltip,
    Avatar
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'core/context/TranslationContext';
// Detect if a given text is mostly composed of Arabic characters
const isMostlyArabic = (text) => {
    if (!text) return false;
    const arabicMatches = text.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/g);
    if (!arabicMatches) return false;

    const ratio = arabicMatches.length / text.length;
    return ratio > 0.4;
};

function HomeJobDetailsPanel({ job, onBookmark, onHide, isBookmarked }) {
    const navigate = useNavigate();
    const { _t } = useTranslation();
    if (!job) {
        return (
            <Paper
                elevation={0}
                sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    boxShadow: 0
                }}
            >
                <Typography variant="body1" color="text.secondary" textAlign="center">
                    {_t('select_a_job_from_the_list_to_view_details')}
                </Typography>
            </Paper>
        );
    }

    const getLocation = () => {
        const parts = [];
        if (job.company?.city) {
            parts.push(job.company.city);
        }
        if (job.company?.country?.name) {
            parts.push(Utils.getDefaultName(job.company.country));
        }
        if (parts.length > 0) {
            return parts.join(', ');
        }
        if (job.address) {
            return job.address;
        }
        return _t('Location not specified');
    };

    const getSalary = () => {
        if (job.salary) {
            return `$${job.salary}${job.pay_rate ? `/${job.pay_rate}` : ''}`;
        }
        if (job.pay_minimum && job.pay_maximum) {
            return `$${job.pay_minimum} - $${job.pay_maximum}${job.pay_rate ? `/${job.pay_rate}` : ''}`;
        }
        return _t('Salary not specified');
    };

    const handleApply = () => {
        navigate(`/job/${job.id}/apply`);
    };

    const handleViewFullDetails = () => {
        navigate(`/job/${job.id}`);
    };

    const normalizedDescription = job?.description ? Utils.normalizeText(job.description) : '';
    const isArabicDescription = isMostlyArabic(normalizedDescription);

    const AVATAR_COLORS = [
        '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828',
        '#00796b', '#5d4037', '#0288d1', '#512da8', '#d81b60'
    ];

    const nameToColor = (name) => {
        let hash = 0;
        for (let i = 0; i < (name?.length || 0); i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    };

    const getCompanyLogoUrl = () => {
        const company = job?.company;
        if (!company?.image) return null;
        if (company.image.startsWith('http')) return company.image;
        if (company.image.startsWith('/')) return HttpService.SiteBase + company.image;
        return `${HttpService.SiteBase}/${company.image}`;
    };

    return (
        <Paper
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 200px)',
                overflow: 'hidden',
                boxShadow: 0
            }}
        >
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                    {(() => {
                        const logoUrl = getCompanyLogoUrl();
                        const title = job.company?.title || '';
                        const initial = title.charAt(0).toUpperCase() || '?';
                        return (
                            <Avatar
                                variant="rounded"
                                src={logoUrl || undefined}
                                alt={title}
                                sx={{
                                    width: 56,
                                    height: 56,
                                    flexShrink: 0,
                                    alignSelf: 'flex-start',
                                    bgcolor: logoUrl ? 'grey.100' : nameToColor(title),
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    fontSize: '1.25rem',
                                    fontWeight: 700,
                                    color: '#fff',
                                    '& .MuiAvatar-img': {
                                        objectFit: 'contain',
                                        objectPosition: 'center'
                                    }
                                }}
                            >
                                {initial}
                            </Avatar>
                        );
                    })()}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" fontWeight={600} gutterBottom>
                            {Utils.normalizeText(job.title)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <BusinessIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                            <Typography variant="body1" color="text.secondary">
                                {job.company?.title || 'Unknown Company'}
                            </Typography>
                            {job.company?.rating && (
                                <Typography variant="caption" color="text.secondary">
                                    {job.company.rating} ★
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                            size="small"
                            onClick={() => onBookmark && onBookmark(job.id, isBookmarked)}
                            color={isBookmarked ? 'primary' : 'default'}
                            sx={!isBookmarked ? { color: 'info.main' } : undefined}
                        >
                            {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                        </IconButton>
                        <Tooltip title={_t('Hide job from results')}>
                            <IconButton
                                size="small"
                                onClick={() => onHide && onHide(job.id)}
                                aria-label={_t('Hide job from results')}
                                sx={{
                                    color: 'text.disabled',
                                    opacity: 0.65,
                                    '&:hover': { color: 'text.secondary', opacity: 1 }
                                }}
                            >
                                <BlockIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                    <Button
                        variant="contained"
                        size="medium"
                        onClick={handleApply}
                        disableElevation
                        sx={{ flex: 1, textTransform: 'none', fontWeight: 600 }}
                    >
                        {_t('apply')}
                    </Button>
                    <Button
                        variant="outlined"
                        size="medium"
                        onClick={handleViewFullDetails}
                        sx={{ flex: 1, textTransform: 'none' }}
                    >
                        {_t('View full details')}
                    </Button>
                </Box>
            </Box>

            {/* Scrollable Content */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 3
                }}
            >
                {/* Job Overview */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                        {_t('Job Overview')}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                        {job.jobtypes && job.jobtypes.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Job Type
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {job.jobtypes.map(jt => Utils.normalizeText(jt.title)).join(', ')}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                {_t('Location')}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <LocationOnIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.1 }} />
                                <Typography variant="body2" fontWeight={500}>
                                    {getLocation()}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                {_t('Salary')}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {getSalary()}
                            </Typography>
                        </Box>
                        {job.experience_level && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    {_t('Experience Level')}
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {job.experience_level}
                                </Typography>
                            </Box>
                        )}
                        {job.created_at && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    {_t('Posted')}
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {Utils.timeAgo(job.created_at)}
                                </Typography>
                            </Box>
                        )}
                        {job.expire_date && (
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    {_t('Expires')}
                                </Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {new Date(job.expire_date).toLocaleDateString()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Job Description */}
                {job.description && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            {_t('Job Description')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography
                            variant="body2"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.6,
                                direction: isArabicDescription ? 'rtl' : 'ltr',
                                textAlign: isArabicDescription ? 'right' : 'left'
                            }}
                            className='kfont'
                        >
                            {normalizedDescription}
                        </Typography>
                    </Box>
                )}

                {/* Schedules */}
                {job.schedules && job.schedules.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            {_t('Schedules')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {job.schedules.map((schedule) => (
                                <Chip
                                    key={schedule.id}
                                    label={Utils.normalizeText(schedule.title)}
                                    variant="outlined"
                                    color="primary"
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Benefits */}
                {job.benefits && job.benefits.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            {_t('Benefits')}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {job.benefits.map((benefit) => (
                                <Chip
                                    key={benefit.id}
                                    label={Utils.normalizeText(benefit.title)}
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                )}

            </Box>
        </Paper>
    );
}

export default HomeJobDetailsPanel;
